'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { LuBoxes, LuSettings, LuX } from 'react-icons/lu';
import { OrganizationSidebar, OrganizationNavItem, OrganizationSidebarSelection } from './OrganizationSidebar';
import { CREATE_VAULT_LABEL } from './OrganizationSidebarNav';
import { OrganizationContentPanel } from './OrganizationContentPanel';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogClose } from '@core/ui/primitives/dialog';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { Separator } from '@core/ui/primitives/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/core/ui/primitives/sidebar';
import { useProfileOrganizations } from '@core/ui/profile/ProfileOrganizationProvider';

interface OrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
interface ProfileVaultSummary {
  id: string;
  slug: string;
  title: string;
  visibility: string;
  role: string;
  privileges: string[];
  /** Set when vault is soft-deleted (in trash) */
  deletedAt?: string | null;
}
interface ProfileOrganizationSummary {
  id: string;
  name: string;
  slug: string;
  organizationRole: string | null;
  privileges: string[];
  vaults: ProfileVaultSummary[];
  logoUrl: string | null;
}
interface OrganizationSummaryResponse {
  organizations: ProfileOrganizationSummary[];
  userIsReadOnly?: boolean;
  organizationLimit?: number;
  ownedOrganizationCount?: number;
}
// Note: No mock nav fallbacks. Render explicit loading/empty/error states instead.
function getInitialSelection(items: OrganizationNavItem[]): OrganizationSidebarSelection {
  const first = items[0];
  if (first?.items && first.items.length > 0) {
    // Prefer first non-deleted vault, fallback to first vault if all deleted
    const activeItems = first.items.filter((v) => !v.isDeleted);
    const primary = activeItems[0] ?? first.items[0];
    return {
      section: first.title,
      label: primary.title,
      vaultId: primary.vaultId ?? null,
      vaultSlug: primary.vaultSlug ?? null,
      organizationId: primary.organizationId ?? null,
    } satisfies OrganizationSidebarSelection;
  }
  return {
    section: first?.title ?? 'Overview',
    label: first?.title ?? 'Overview',
  } satisfies OrganizationSidebarSelection;
}
function formatOrganizationSubtitle(org: ProfileOrganizationSummary): string {
  const role = org.organizationRole ? org.organizationRole.toLowerCase() : null;
  if (role) {
    return `${role.charAt(0).toUpperCase()}${role.slice(1)} • ${org.slug}`;
  }
  return org.slug;
}
export function OrganizationDialog({ open, onOpenChange }: OrganizationDialogProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { currentOrganization } = useProfileOrganizations();
  const [navSelection, setNavSelection] = useState<OrganizationSidebarSelection | null>(null);
  const [organizations, setOrganizations] = useState<ProfileOrganizationSummary[]>([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null);
  // Start in loading state to avoid flash of empty state before fetch begins
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [userIsReadOnly, setUserIsReadOnly] = useState(false);
  const [organizationLimit, setOrganizationLimit] = useState<number | null>(null);
  const [serverOwnedCount, setServerOwnedCount] = useState<number | null>(null);

  const applyOrganizations = useCallback((list: ProfileOrganizationSummary[], preferredId?: string | null) => {
    setOrganizations(list);
    setActiveOrganizationId((previous) => {
      if (preferredId && list.some((org) => org.id === preferredId)) {
        return preferredId;
      }
      if (previous && list.some((org) => org.id === previous)) {
        return previous;
      }
      return list[0]?.id ?? null;
    });
  }, []);
  const reloadOrganizations = useCallback(
    async (options?: { selectOrganizationId?: string | null }) => {
      try {
        setLoading(true);
        setError(null);
        // Include deleted vaults for recovery UI in org settings
        const response = await fetch('/api/vaults/me?includeDeleted=1');
        if (!response.ok) {
          throw new Error('Unable to load organization overview.');
        }
        const payload = (await response.json()) as OrganizationSummaryResponse;
        const sorted = [...(payload.organizations ?? [])].sort((a, b) => a.name.localeCompare(b.name));
        applyOrganizations(sorted, options?.selectOrganizationId ?? null);
        setUserIsReadOnly(payload.userIsReadOnly ?? false);
        setOrganizationLimit(typeof payload.organizationLimit === 'number' ? payload.organizationLimit : null);
        setServerOwnedCount(typeof payload.ownedOrganizationCount === 'number' ? payload.ownedOrganizationCount : null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load organization overview.');
      } finally {
        setLoading(false);
      }
    },
    [applyOrganizations]
  );
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Include deleted vaults for recovery UI in org settings
        const response = await fetch('/api/vaults/me?includeDeleted=1', { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load organization overview.');
        const payload = (await response.json()) as OrganizationSummaryResponse;
        if (cancelled) return;
        const sorted = [...(payload.organizations ?? [])].sort((a, b) => a.name.localeCompare(b.name));
        applyOrganizations(sorted, currentOrganization?.id ?? null);
        setUserIsReadOnly(payload.userIsReadOnly ?? false);
        setOrganizationLimit(typeof payload.organizationLimit === 'number' ? payload.organizationLimit : null);
        setServerOwnedCount(typeof payload.ownedOrganizationCount === 'number' ? payload.ownedOrganizationCount : null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load organization overview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [applyOrganizations, currentOrganization?.id, open]);
  const activeOrganization = useMemo(() => {
    if (!organizations.length) {
      return null;
    }
    if (activeOrganizationId) {
      const found = organizations.find((org) => org.id === activeOrganizationId);
      if (found) {
        return found;
      }
    }
    return organizations[0];
  }, [activeOrganizationId, organizations]);
  const vaultNavItems = useMemo(() => {
    if (!activeOrganization || !activeOrganization.vaults.length) {
      return [];
    }
    return activeOrganization.vaults.map((vault) => ({
      title: vault.title,
      url: `#vault-${vault.slug}`,
      vaultId: vault.id,
      vaultSlug: vault.slug,
      organizationId: activeOrganization.id,
      isDeleted: Boolean(vault.deletedAt),
    }));
  }, [activeOrganization]);
  // Ensure nav menu follows organization changes explicitly
  useEffect(() => {
    if (!organizations.length) {
      return;
    }
    const org = activeOrganizationId ? organizations.find((o) => o.id === activeOrganizationId) : organizations[0];
    if (!org) return;
    // If selection already targets this org's vaults, the create wizard, or Organization Settings, keep it
    if (navSelection?.section === 'Organization Settings') {
      return;
    }
    if (
      navSelection?.organizationId === org.id &&
      navSelection.section === 'Vaults' &&
      (navSelection.label === CREATE_VAULT_LABEL || Boolean(navSelection.vaultId))
    ) {
      return;
    }
    if (org.vaults.length > 0) {
      // Prefer first non-deleted vault, fallback to first vault if all deleted
      const activeVaults = org.vaults.filter((v) => !v.deletedAt);
      const first = activeVaults[0] ?? org.vaults[0];
      setNavSelection({
        section: 'Vaults',
        label: first.title,
        vaultId: first.id,
        vaultSlug: first.slug,
        organizationId: org.id,
      });
    } else {
      setNavSelection({ section: 'Organization Settings', label: 'Organization Settings' });
    }
  }, [activeOrganizationId, organizations, navSelection?.organizationId, navSelection?.section]);
  useEffect(() => {
    const navSource: OrganizationNavItem[] = [
      {
        title: 'Vaults',
        url: '#vaults',
        icon: LuBoxes,
        items: vaultNavItems,
      },
      {
        title: 'Organization Settings',
        url: '#organization-settings',
        icon: LuSettings,
      },
    ];
    if (!navSelection) {
      if (organizations.length) {
        setNavSelection(getInitialSelection(navSource));
      }
      return;
    }
    const vaultSection = navSource.find((item) => item.title === 'Vaults');
    const vaultLabels = vaultSection?.items ?? [];
    if (navSelection.section === 'Vaults') {
      if (navSelection.label === CREATE_VAULT_LABEL) {
        return;
      }
      if (!vaultLabels.length) {
        setNavSelection({ section: 'Organization Settings', label: 'Organization Settings' });
        return;
      }
      const matchingVault = vaultLabels.find((item) => {
        if (navSelection.vaultId) {
          return item.vaultId === navSelection.vaultId;
        }
        return item.title === navSelection.label;
      });
      if (!matchingVault) {
        // Prefer first non-deleted vault, fallback to first vault if all deleted
        const activeVaults = vaultLabels.filter((v) => !v.isDeleted);
        const fallback = activeVaults[0] ?? vaultLabels[0];
        setNavSelection({
          section: 'Vaults',
          label: fallback.title,
          vaultId: fallback.vaultId ?? null,
          vaultSlug: fallback.vaultSlug ?? null,
          organizationId: fallback.organizationId ?? null,
        });
      }
    }
  }, [navSelection, organizations.length, vaultNavItems]);
  const sidebarNavItems = useMemo(() => {
    const baseItems: OrganizationNavItem[] = [
      {
        title: 'Vaults',
        url: '#vaults',
        icon: LuBoxes,
        items: vaultNavItems,
      },
      {
        title: 'Organization Settings',
        url: '#organization-settings',
        icon: LuSettings,
      },
    ];
    return baseItems.map((item) => ({
      ...item,
      isActive: navSelection?.section === item.title,
    }));
  }, [navSelection?.section, vaultNavItems]);
  const organizationOptions = useMemo(() => {
    if (!organizations.length) {
      return undefined;
    }
    return organizations.map((org) => ({
      id: org.id,
      name: org.name,
      plan: formatOrganizationSubtitle(org),
      slug: org.slug,
      logoUrl: org.logoUrl ?? null,
    }));
  }, [organizations]);
  const ownedOrganizationsCount = useMemo(() => {
    if (typeof serverOwnedCount === 'number') {
      return serverOwnedCount;
    }
    if (!organizations.length) {
      return 0;
    }
    return organizations.filter((org) => org.organizationRole?.toLowerCase() === 'owner').length;
  }, [organizations, serverOwnedCount]);
  const canCreateOrganization =
    typeof organizationLimit === 'number' ? ownedOrganizationsCount < organizationLimit : true;
  const handleCreateOrganization = useCallback(
    async (name: string) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        throw new Error('Organization name is required.');
      }
      const response = await fetch('/api/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = typeof payload?.error === 'string' ? payload.error : 'Failed to create organization.';
        throw new Error(message);
      }
      const organization = payload?.organization;
      if (!organization?.id) {
        throw new Error('Failed to create organization.');
      }
      await reloadOrganizations({ selectOrganizationId: organization.id });
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vaults:updated'));
        }
      } catch {}
      return organization;
    },
    [reloadOrganizations]
  );
  const handleVaultCreated = useCallback(
    async (vault: { id: string; slug: string; title: string; organizationId: string }) => {
      await reloadOrganizations({ selectOrganizationId: vault.organizationId });
      setNavSelection({
        section: 'Vaults',
        label: vault.title,
        vaultId: vault.id,
        vaultSlug: vault.slug,
        organizationId: vault.organizationId,
      });
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vaults:updated'));
        }
      } catch {}
    },
    [reloadOrganizations]
  );
  const handleVaultDeleted = useCallback(async () => {
    // Check if user is currently viewing the deleted vault and redirect to org root
    const deletedVaultSlug = navSelection?.vaultSlug;
    if (deletedVaultSlug && pathname && activeOrganization) {
      const pathSegments = pathname.split('/').filter(Boolean);
      // Check if current path contains the deleted vault slug
      // Patterns: /{orgSlug}/{vaultSlug}/... or /v/{vaultSlug}/...
      const isInDeletedVault =
        (pathSegments.length >= 2 && pathSegments[1] === deletedVaultSlug) ||
        (pathSegments[0] === 'v' && pathSegments[1] === deletedVaultSlug);

      if (isInDeletedVault) {
        // Redirect to organization root
        router.push(`/${activeOrganization.slug}`);
      }
    }

    await reloadOrganizations();
    setNavSelection({ section: 'Organization Settings', label: 'Organization Settings' });
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vaults:updated'));
      }
    } catch {}
  }, [reloadOrganizations, navSelection?.vaultSlug, pathname, activeOrganization, router]);

  const handleOrganizationUpdated = useCallback((organization: { id: string; name: string; slug: string }) => {
    // Update the organization in our local state
    setOrganizations((prev) =>
      prev.map((org) =>
        org.id === organization.id ? { ...org, name: organization.name, slug: organization.slug } : org
      )
    );
  }, []);

  const handleOrganizationDeleted = useCallback(async () => {
    await reloadOrganizations();
    // Select the first available organization, or show empty state
    setNavSelection(null);
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vaults:updated'));
      }
    } catch {}
  }, [reloadOrganizations]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'w-full sm:max-w-5xl h-[50rem] min-h-[50rem] max-h-[50rem] overflow-hidden gap-0 p-0 shadow-2xl sm:rounded-3xl'
        )}>
        <DialogTitle className='sr-only'>Organization dialog</DialogTitle>
        <DialogDescription className='sr-only'>Manage organization vaults and settings.</DialogDescription>
        <SidebarProvider>
          <div className='flex h-full min-h-0 w-full bg-background max-h-[50rem]'>
            <OrganizationSidebar
              navItems={sidebarNavItems}
              organizations={organizationOptions}
              activeOrganizationId={activeOrganizationId}
              onOrganizationChange={setActiveOrganizationId}
              selectedNav={navSelection}
              onNavSelect={setNavSelection}
              loading={loading}
              error={error}
              onOrganizationCreate={handleCreateOrganization}
              canCreateOrganization={canCreateOrganization}
              organizationLimit={organizationLimit ?? undefined}
              ownedOrganizationCount={ownedOrganizationsCount}
            />
            <SidebarInset className='flex min-h-0 flex-1 flex-col'>
              <header className='flex py-7 h-8 items-center gap-2 border-b border-gray-200/60 px-4 dark:border-gray-800/80'>
                <SidebarTrigger className='-ml-1 h-8 w-8' />
                <Separator orientation='vertical' className='mr-3 h-6 w-px bg-gray-300 dark:bg-gray-700' />
                <div className='flex flex-col'>
                  {navSelection ? (
                    <>
                      <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                        {navSelection.section}
                      </span>
                      <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                        {navSelection.section === 'Organization Settings'
                          ? activeOrganization?.name ?? 'Organization'
                          : navSelection.label}
                      </span>
                    </>
                  ) : loading ? (
                    <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      Loading…
                    </span>
                  ) : error ? (
                    <span className='text-xs font-semibold uppercase tracking-wide text-rose-600'>Error</span>
                  ) : (
                    <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Empty</span>
                  )}
                </div>
                <div className='flex flex-1 items-center justify-end gap-1'>
                  {headerActions}
                  <DialogClose asChild>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      aria-label='Close'>
                      <LuX className='h-3.5 w-3.5' />
                    </button>
                  </DialogClose>
                </div>
              </header>
              <div className='flex flex-1 min-h-0 flex-col overflow-hidden p-4 pt-3'>
                {error && <div className='text-sm text-rose-600'>{error}</div>}
                {!error && !loading && !navSelection && (
                  <div className='text-sm text-muted-foreground'>No organizations found.</div>
                )}
                {!error && navSelection && (
                  <OrganizationContentPanel
                    selection={navSelection}
                    navItems={sidebarNavItems}
                    organizations={organizations.map((org) => ({
                      id: org.id,
                      name: org.name,
                      slug: org.slug,
                      vaults: org.vaults.map((v) => ({ id: v.id, role: v.role, privileges: v.privileges })),
                    }))}
                    activeOrganization={
                      activeOrganization
                        ? {
                            id: activeOrganization.id,
                            name: activeOrganization.name,
                            slug: activeOrganization.slug,
                            role: activeOrganization.organizationRole,
                            privileges: activeOrganization.privileges,
                          }
                        : null
                    }
                    userIsReadOnly={userIsReadOnly}
                    onSelect={setNavSelection}
                    onVaultCreated={handleVaultCreated}
                    onVaultDeleted={handleVaultDeleted}
                    onOrganizationUpdated={handleOrganizationUpdated}
                    onOrganizationDeleted={handleOrganizationDeleted}
                    setHeaderActions={setHeaderActions}
                  />
                )}
              </div>
            </SidebarInset>
          </div>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  );
}
